import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Plus, Trash2, Wine, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { utmPayload } from '../lib/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Wine catalog organized by category
const WINE_CATEGORIES = {
  bronze: {
    name: 'Bronze',
    price: 150,
    description: 'Easy Drinking',
    wines: [
      'Pinot Grigio', 'Sauvignon Blanc', 'Chardonnay', 'Riesling', 'California White',
      'Pinot Noir', 'Cabernet Sauvignon', 'Vieux Chateau du Roi', 'Shiraz', 'Merlot'
    ]
  },
  silver: {
    name: 'Silver',
    price: 182,
    description: 'Premium Selection',
    wines: [
      'California Muscat O/D', 'Germany Riesling O/D', 'South Africa Chenin Blanc',
      'California Chardonnay', 'California White Zinfandel', 'Germany Gewürztraminer O/D',
      'Italy Verdicchio', 'Ontario Pinot Grigio', 'Italy Pinot Grigio', 'Ontario Sauvignon Blanc',
      'Germany Müller-Thurgau O/D', 'Chile Malbec', 'British Columbia Pinot Noir',
      'Okanagan Meritage', 'Italy Nebbiolo', 'Washington Merlot', 'Australia Cabernet Sauvignon',
      'California Syrah Style', 'California Cabernet Sauvignon', 'California Zinfandel', 'Chile Merlot Style'
    ]
  },
  gold: {
    name: 'Gold',
    price: 210,
    description: "Winemaker's Collection",
    wines: [
      'German Gewürztraminer', 'Italy Bella Bianco O/D', 'Argentina Trio: Viognier/Riesling/Chardonnay',
      'Italy Pinot Grigio', 'Australia Chardonnay', 'New Zealand Sauvignon Blanc',
      'Australia Viognier Pinot Gris', 'France Merlot', 'California Pinot Noir', 'Chile Malbec',
      'Italy Sangiovese', 'Australia Shiraz', 'Amarone', 'Australia Cabernet Sauvignon',
      'California Cabernet/Syrah/Zinfandel', 'Italy Nebbiolo', 'Italy Valpola'
    ]
  },
  platinum: {
    name: 'Platinum',
    price: 230,
    description: 'Reserve Series',
    wines: [
      'Chile Chardonnay', "Winemaker's Trio White (Pinot Grigio/Sauv Blanc/Muscat)",
      'Italy Pinot Grigio', 'South Africa Sauvignon Blanc', 'Chile Merlot', 'Italy Zinfandel',
      'Australia Cabernet Sauvignon', 'Chile Carménère', 'Australia Pinot Noir', 'Australia Shiraz',
      'Spain Grenache Syrah', 'Chile Malbec', 'Italy Amarone', 'Italy Rosso Grande Excellente',
      'Italy Super Tuscan', 'Italy Zinfandel', "Winemaker's Trio Red (Cab Sauv/Syrah/Zinfandel)"
    ]
  },
  fruitWines: {
    name: 'Fruit Wines',
    price: 160,
    description: 'Fresh & Vibrant',
    wines: [
      'Wild Watermelon', 'Very Black Cherry', 'Peach Perfection', 'Green Apple Delight',
      'Mango Dragon Fruit Lemonade', 'Tropical Lime', 'Strawberry Sensation', 'Seville Orange Sangria',
      "Rockin' Raspberry Rosé", 'Blueberry Bliss', 'Cranberry Craze', 'Blackberry Blast',
      'Acai Raspberry Rapture'
    ]
  },
  halfBatch: {
    name: 'Half Batch',
    price: 109,
    description: '5-Week Wines Only (15 bottles)',
    wines: ['Cabernet Sauvignon', 'Merlot', 'Pinot Grigio']
  }
};

const OrderNow = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContact: '',
    yeastActivation: '',
    notes: '',
    referralCode: ''
  });

  const [wineSelections, setWineSelections] = useState([
    { category: '', wineName: '', quantity: 1, batchSize: 'full', price: 0 }
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleWineChange = (index, field, value) => {
    const updated = [...wineSelections];
    updated[index][field] = value;

    // Update price when category changes
    if (field === 'category') {
      updated[index].price = WINE_CATEGORIES[value]?.price || 0;
      updated[index].wineName = '';
    }

    setWineSelections(updated);
  };

  const addWineSelection = () => {
    setWineSelections([...wineSelections, { category: '', wineName: '', quantity: 1, batchSize: 'full', price: 0 }]);
  };

  const removeWineSelection = (index) => {
    if (wineSelections.length > 1) {
      setWineSelections(wineSelections.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return wineSelections.reduce((total, wine) => {
      return total + (wine.price * wine.quantity);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast({ title: 'Missing Information', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (!formData.preferredContact) {
      toast({ title: 'Contact Method Required', description: 'Please select your preferred contact method', variant: 'destructive' });
      return;
    }

    if (!formData.yeastActivation) {
      toast({ title: 'AGCO Consent Required', description: 'Please select your yeast activation preference', variant: 'destructive' });
      return;
    }

    const hasInvalidWines = wineSelections.some(wine => !wine.category || !wine.wineName);
    if (hasInvalidWines) {
      toast({ title: 'Wine Selection Required', description: 'Please select wines for all entries', variant: 'destructive' });
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        ...formData,
        ...utmPayload(),
        wineSelections: wineSelections.map(wine => ({
          wineName: wine.wineName,
          category: WINE_CATEGORIES[wine.category]?.name || wine.category,
          price: wine.price,
          quantity: wine.quantity,
          batchSize: wine.batchSize
        }))
      };

      const response = await axios.post(`${API}/orders/`, orderData);

      if (response.data.success) {
        setSubmitted(true);
        toast({
          title: 'Order Submitted!',
          description: 'We will contact you shortly to confirm your order.',
        });
      }
    } catch (error) {
      console.error('Order submission error:', error);
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Please try again or call us directly.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16" data-testid="order-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-green-500 shadow-2xl">
            <CardContent className="pt-12 pb-12 text-center">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Thank You for Your Order Request!
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We will contact you shortly to confirm your order, collect payment, and set up a time to activate your wine.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Your order is not finalized until confirmed by The Lokal Winery.
                </p>
              </div>
              <p className="text-gray-600 mb-8">
                You should receive a confirmation email at <strong>{formData.email}</strong>
              </p>
              <div className="space-y-3">
                <Button onClick={() => window.location.href = '/'} className="w-full sm:w-auto bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900">
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12" data-testid="order-now-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="order-now-title">
            Order Your Wine
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select your wines and we'll contact you to confirm your order, collect payment, and set up your winemaking session.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    data-testid="order-firstname-input"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    data-testid="order-lastname-input"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="order-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="order-phone-input"
                    type="tel"
                    placeholder="705-123-4567"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="preferredContact">Preferred Contact Method *</Label>
                <Select value={formData.preferredContact} onValueChange={(value) => handleInputChange('preferredContact', value)}>
                  <SelectTrigger data-testid="order-preferred-contact-trigger">
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email" data-testid="contact-option-email">Email</SelectItem>
                    <SelectItem value="phone" data-testid="contact-option-phone">Phone</SelectItem>
                    <SelectItem value="either" data-testid="contact-option-either">Either</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Wine Selections */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wine className="w-5 h-5" />
                Wine Selections
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {wineSelections.map((wine, index) => (
                <div key={index} className="p-4 border-2 border-gray-200 rounded-lg space-y-4 relative">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-900">Wine #{index + 1}</h3>
                    {wineSelections.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeWineSelection(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div>
                    <Label>Wine Category *</Label>
                    <Select value={wine.category} onValueChange={(value) => handleWineChange(index, 'category', value)}>
                      <SelectTrigger data-testid={`wine-category-trigger-${index}`}>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bronze" data-testid={`wine-category-option-bronze-${index}`}>Bronze - $150 (Easy Drinking)</SelectItem>
                        <SelectItem value="silver" data-testid={`wine-category-option-silver-${index}`}>Silver - $182 (Premium Selection)</SelectItem>
                        <SelectItem value="gold" data-testid={`wine-category-option-gold-${index}`}>Gold - $210 (Winemaker's Collection)</SelectItem>
                        <SelectItem value="platinum" data-testid={`wine-category-option-platinum-${index}`}>Platinum - $230 (Reserve Series)</SelectItem>
                        <SelectItem value="fruitWines" data-testid={`wine-category-option-fruit-${index}`}>Fruit Wines - $160 (Fresh & Vibrant)</SelectItem>
                        <SelectItem value="halfBatch" data-testid={`wine-category-option-halfbatch-${index}`}>🍷 Half Batch - $109 (15 bottles, 5-week wines only)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {wine.category && (
                    <div>
                      <Label>Select Wine *</Label>
                      <Select value={wine.wineName} onValueChange={(value) => handleWineChange(index, 'wineName', value)}>
                        <SelectTrigger data-testid={`wine-name-trigger-${index}`}>
                          <SelectValue placeholder="Choose your wine" />
                        </SelectTrigger>
                        <SelectContent>
                          {WINE_CATEGORIES[wine.category]?.wines.map((wineName) => (
                            <SelectItem
                              key={wineName}
                              value={wineName}
                              data-testid={`wine-name-option-${index}-${wineName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            >
                              {wineName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        min="1"
                        data-testid={`wine-quantity-${index}`}
                        value={wine.quantity}
                        onChange={(e) => handleWineChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div>
                      <Label>Batch Size *</Label>
                      <Select
                        value={wine.batchSize}
                        onValueChange={(value) => handleWineChange(index, 'batchSize', value)}
                        disabled={wine.category === 'halfBatch'}
                      >
                        <SelectTrigger data-testid={`wine-batchsize-trigger-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full" data-testid={`wine-batchsize-option-full-${index}`}>Full (30 bottles)</SelectItem>
                          {wine.category === 'halfBatch' && (
                            <SelectItem value="half" data-testid={`wine-batchsize-option-half-${index}`}>Half (15 bottles)</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {wine.price > 0 && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm text-gray-600">
                        Subtotal: <strong className="text-lg text-[#FDB913]">${(wine.price * wine.quantity).toFixed(2)}</strong>
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addWineSelection}
                data-testid="order-add-wine-btn"
                className="w-full border-dashed border-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Wine
              </Button>

              {wineSelections.length > 0 && wineSelections[0].price > 0 && (
                <div className="bg-[#FDB913] bg-opacity-10 border-2 border-[#FDB913] p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Estimated Total:</span>
                    <span className="text-2xl font-bold text-[#FDB913]">${calculateTotal().toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">* Wine is tax-free</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AGCO Yeast Activation */}
          <Card>
            <CardHeader>
              <CardTitle>AGCO Yeast Activation (Required)</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={formData.yeastActivation} onValueChange={(value) => handleInputChange('yeastActivation', value)}>
                <div className="flex items-start space-x-3 p-4 border-2 rounded-lg hover:border-[#FDB913] transition-colors">
                  <RadioGroupItem value="consent" id="consent" data-testid="yeast-consent-radio" />
                  <Label htmlFor="consent" className="font-normal cursor-pointer flex-1">
                    I consent to The Lokal Winery activating my yeast on my behalf in accordance with AGCO requirements.
                  </Label>
                </div>
                <div className="flex items-start space-x-3 p-4 border-2 rounded-lg hover:border-[#FDB913] transition-colors">
                  <RadioGroupItem value="in_person" id="in_person" data-testid="yeast-inperson-radio" />
                  <Label htmlFor="in_person" className="font-normal cursor-pointer flex-1">
                    I would like to come in and activate my wine myself.
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  rows="4"
                  placeholder="Any special requests or questions..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="referralCode">Referral Code (Optional)</Label>
                <Input
                  id="referralCode"
                  placeholder="Enter referral code if you have one"
                  value={formData.referralCode}
                  onChange={(e) => handleInputChange('referralCode', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              type="submit"
              data-testid="order-submit-btn"
              disabled={submitting}
              className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 text-lg px-8 py-6 font-bold"
            >
              {submitting ? 'Submitting Order...' : 'Submit Order Request'}
            </Button>
          </div>

          <p className="text-center text-sm text-gray-500">
            By submitting this form, you agree to be contacted by The Lokal Winery to finalize your order.
          </p>
        </form>
      </div>
    </div>
  );
};

export default OrderNow;
